const CHECKOUT_ORIGIN = "https://checkout.wellnesshoma.co.uk";
const VARIANT_PREFIX = "gid://shopify/ProductVariant/";

export function normalizeShopifyCart(cart) {
  if (!cart || !Array.isArray(cart.items) || cart.items.length < 1 || cart.items.length > 100) {
    throw new Error("EMPTY_OR_INVALID_CART");
  }
  const hasDiscount = (cart.discount_codes ?? []).some((discount) => discount?.applicable)
    || (cart.cart_level_discount_applications ?? []).length > 0
    || cart.items.some((item) => item && (
      (Number.isSafeInteger(item.original_price) && Number.isSafeInteger(item.final_price) && item.original_price !== item.final_price)
      || (Number.isSafeInteger(item.original_line_price) && Number.isSafeInteger(item.final_line_price) && item.original_line_price !== item.final_line_price)
      || (item.discounts?.length ?? 0) > 0
    ));
  const hasUnsupportedItems = cart.items.some((item) => item && (
    (item.properties && Object.keys(item.properties).length > 0)
    || item.selling_plan_allocation
    || item.requires_shipping === false
  ));
  if (hasDiscount || hasUnsupportedItems) throw new Error("UNSUPPORTED_CART");

  const combined = new Map();
  for (const item of cart.items) {
    if (!item || !Number.isSafeInteger(item.variant_id) || item.variant_id < 1
      || !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 99) {
      throw new Error("INVALID_CART_LINE");
    }
    const variantId = `${VARIANT_PREFIX}${item.variant_id}`;
    const quantity = (combined.get(variantId) ?? 0) + item.quantity;
    if (quantity > 99) throw new Error("INVALID_CART_LINE");
    combined.set(variantId, quantity);
  }
  return [...combined].map(([variantId, quantity]) => ({ variantId, quantity }));
}

export function checkoutUrl(lines) {
  if (!Array.isArray(lines) || lines.length < 1 || lines.length > 100) throw new Error("INVALID_CART_LINE");
  for (const line of lines) {
    if (!line || typeof line.variantId !== "string" || !line.variantId.startsWith(VARIANT_PREFIX)
      || !/^[0-9]+$/.test(line.variantId.slice(VARIANT_PREFIX.length))
      || !Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 99) throw new Error("INVALID_CART_LINE");
  }
  const url = new URL("/checkout", CHECKOUT_ORIGIN);
  url.searchParams.set("lines", JSON.stringify(lines));
  return url.href;
}

export async function productCheckoutUrl(formData, fetchImpl = fetch) {
  if (!formData || typeof formData.get !== "function" || typeof formData.entries !== "function") {
    throw new Error("PRODUCT_FORM_INVALID");
  }
  const variantId = String(formData.get("id") ?? "").trim();
  const quantityText = String(formData.get("quantity") ?? "1").trim();
  const quantity = Number(quantityText);
  if (!/^[0-9]+$/.test(variantId) || !/^[0-9]+$/.test(quantityText)
    || !Number.isInteger(quantity) || quantity < 1 || quantity > 99) throw new Error("INVALID_CART_LINE");
  const hasUnsupportedOptions = [...formData.entries()].some(([name, value]) =>
    ((name === "selling_plan" || name.startsWith("properties[")) && String(value).trim() !== ""));
  if (hasUnsupportedOptions) throw new Error("UNSUPPORTED_CART");

  const cartOptions = { credentials: "same-origin", headers: { Accept: "application/json" } };
  const currentResponse = await fetchImpl("/cart.js", cartOptions);
  if (!currentResponse.ok) throw new Error("CART_LOAD_FAILED");
  const currentCart = await currentResponse.json();
  if (!currentCart || !Array.isArray(currentCart.items) || currentCart.items.length > 100) {
    throw new Error("EMPTY_OR_INVALID_CART");
  }
  if (currentCart.items.length === 0 && (
    (currentCart.discount_codes ?? []).some((discount) => discount?.applicable)
    || (currentCart.cart_level_discount_applications ?? []).length > 0
  )) throw new Error("UNSUPPORTED_CART");
  const existingLines = currentCart.items.length ? normalizeShopifyCart(currentCart) : [];
  const targetVariantId = `${VARIANT_PREFIX}${variantId}`;
  const existingQuantity = existingLines.find((line) => line.variantId === targetVariantId)?.quantity ?? 0;
  if (existingQuantity + quantity > 99 || (existingQuantity === 0 && existingLines.length >= 100)) {
    throw new Error("INVALID_CART_LINE");
  }

  const addForm = new FormData();
  addForm.set("id", variantId);
  addForm.set("quantity", String(quantity));
  const addResponse = await fetchImpl("/cart/add.js", {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    body: addForm,
  });
  if (!addResponse.ok) throw new Error("PRODUCT_ADD_FAILED");

  const finalResponse = await fetchImpl("/cart.js", cartOptions);
  if (!finalResponse.ok) throw new Error("CART_LOAD_FAILED");
  const finalCart = await finalResponse.json();
  const lines = normalizeShopifyCart(finalCart);
  const addedLine = lines.find((line) => line.variantId === targetVariantId);
  if (!addedLine || addedLine.quantity < existingQuantity + quantity) throw new Error("PRODUCT_ADD_FAILED");
  return checkoutUrl(lines);
}

export function checkoutErrorMessage(code) {
  if (code === "UNSUPPORTED_CART") return "This cart includes a discount or customized item that secure checkout can’t currently process. Remove the unsupported item or discount to continue.";
  if (code === "PRODUCT_ADD_FAILED") return "We couldn’t add this product to your cart. Check availability and try again.";
  if (code === "INVALID_CART_LINE") return "Please check the selected quantity and try again.";
  if (code === "PRODUCT_FORM_INVALID") return "We couldn’t read this product selection. Refresh the page and try again.";
  return "We couldn’t load this cart. Refresh the page and try again.";
}

export function attachCheckoutHandler(documentRef = document, {
  fetchImpl = fetch,
  navigate = (url) => window.location.assign(url),
  formDataFactory = (form) => new FormData(form),
} = {}) {
  documentRef.addEventListener("click", async (event) => {
    const target = event.target;
    const productButton = target?.closest?.("[data-novabike-product-checkout]") ?? null;
    if (productButton) {
      if (productButton.disabled) return;
      event.preventDefault();
      const container = productButton.closest("[data-novabike-product-checkout-container]");
      const status = container?.querySelector("[data-novabike-product-checkout-status]");
      const originalLabel = productButton.textContent;
      productButton.disabled = true;
      productButton.textContent = "Opening secure checkout…";
      if (status) {
        status.textContent = "";
        status.classList.add("hidden");
      }
      try {
        const form = productButton.closest("form");
        if (!form) throw new Error("PRODUCT_FORM_INVALID");
        const destination = await productCheckoutUrl(formDataFactory(form), fetchImpl);
        navigate(destination);
      } catch (error) {
        productButton.disabled = false;
        productButton.textContent = originalLabel;
        if (status) {
          status.textContent = checkoutErrorMessage(error?.message);
          status.classList.remove("hidden");
        }
      }
      return;
    }

    const button = target?.closest?.("[data-novabike-checkout]") ?? null;
    if (!button || button.disabled) return;
    event.preventDefault();
    const container = button.closest("[data-novabike-checkout-container]");
    const status = container?.querySelector("[data-novabike-checkout-status]");
    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = "Opening secure checkout…";
    if (status) status.textContent = "Loading your current Shopify cart…";
    try {
      const response = await fetchImpl("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("CART_LOAD_FAILED");
      const cart = await response.json();
      const lines = normalizeShopifyCart(cart);
      navigate(checkoutUrl(lines));
    } catch (error) {
      button.disabled = false;
      button.textContent = originalLabel;
      if (status) status.textContent = checkoutErrorMessage(error?.message);
    }
  });
}

if (typeof document !== "undefined") attachCheckoutHandler();
